import { reactive } from 'vue';
import { pageFromHash, resolveChromeMode, groupCartProducts, cartQuantityText, francoSummary } from '@/utils/domain/legacyNavModel';
import { buildMenuGroups, hasAccessToSales, HOME_MENU } from '@/utils/domain/legacyMenuRules';
import { dateOnly, addDays, buildVisitList, classifyAppointment, isAppointmentReady } from '@/utils/domain/legacyVisitRules';
import { getMenus } from '@/api/core/menu';
import { getRolesMenu } from '@/api/core/role';
import { getNotifications } from '@/api/communication/notification';
import { resetLegacyDialogs } from './legacyDialogs';
import { attachLegacyNotificationPoller, detachLegacyNotificationPoller, pollLegacyNotificationsNow } from './legacyNotificationPoller';
import { log } from '@/utils/core/vueLogger';
import { isOnline, onConnectivityChange } from '@/sync/connectivity';

// Reactive mirror of the legacy navigation state for the Vue chrome (src/components/nav), which
// replaced the legacy nav / sidebar components and the ones they hosted (notification, visit
// preview, cart preview).
//
// Knockout observables read inside a Vue computed register no dependency in either system, so the
// state is rebuilt from the legacy business modules and storage on the amplify topics that change
// it, and pushed as plain snapshots into `legacyNav`. A new legacy document is loaded on every
// iframe load, so its window is re-attached each time.
//
// Live legacy objects never enter the reactive state: actions look them up by key in `rawItems`.

const POLL_MS = 1000;
const REFRESH_DEBOUNCE_MS = 60;
// Second pass once the legacy modules that also react to evtAppSwitching (shoppingCart, the boot
// navigation in eagle.js, the OPTIONS synchronization) have settled.
const APP_SWITCH_SETTLE_MS = 700;
const ASSORT_RETURN_ORDER_TYPE = 8;
// notification.js getIcon(type, newVersion=true) - Vue always renders the "newVersion" (Material) set.
const NOTIFICATION_ICONS = { 0: 'notifications', 1: 'folder_zip', 2: 'chat_bubble', 3: 'info' };

const emptyShortcut = () => ({ visible: false, count: 0 });

const initialState = () => ({
	attached: false,
	menusReady: false,
	page: 'other',
	loggedIn: false,
	hiddenApp: false,
	delegation: null,
	delegationFrameOpen: false,
	chromeMode: 'hidden',
	// A chrome dialog the legacy flow asks for (the new release note popup on the home view).
	requestedDialog: null,
	env: '',
	version: '',
	isLocalhost: false,
	online: true,
	updateReady: false,
	extensionUpdate: false,
	appIsReady: false,
	synchronizing: false,
	vueWidgets: false,
	agendaSynchAllowed: false,
	agendaCalendars: [],
	sfaActivated: false,
	storageSize: { sizeBase: 0, sizeExtraSales: 0, sizeExtraDoc: 0 },
	user: { firstName: '', lastName: '', userName: '', email: '', organisationLabel: '', locale: '' },
	customer: { logo: null, name: '' },
	menus: { home: null, agenda: null, myPos: null, sfa: [], sales: [], doc: [] },
	flags: { delegation: false, tokenSales: false, tokenDoc: false, hasAccessToSales: false, assortOrderActivated: false },
	shortcuts: { draft: emptyShortcut(), offline: emptyShortcut(), offlineErrors: emptyShortcut() },
	notifications: [],
	visits: { current: [], preparation: [], late: [] },
	cart: { regular: [], rsw: [], assort: [], count: 0 },
});

export const legacyNav = reactive(initialState());

export const rawItems = {
	notifications: new Map(),
	visits: new Map(),
	cart: new Map(),
};

let win = null;
let pollTimer = null;
let refreshTimer = null;
let settleTimer = null;
let geoCodeTimer = null;
let boundAmplify = null;
let amplifyHandlers = [];
let observers = [];
let hashListener = null;
let stopConnectivityWatch = null;
let mainUpdateSignal = false;
// Mirrors of the amplify topics the legacy nav.js listened to for these flags: each one was only
// ever set from its topic.
let extensionUpdateSignal = false;
let appIsReadySignal = false;
let agendaSynchAllowedSignal = false;
const dismissedNotifications = new Set();
// sfaHelper.saveAppointmentSyncError() POSTs a log line: once per appointment and legacy document,
// refreshVisits() runs far more often than visitPreview.js init() did.
const reportedMissingPos = new Set();

export const safe = (fn, fallback = null) => {
	try {
		const value = fn();
		return value === undefined ? fallback : value;
	} catch {
		return fallback;
	}
};

export const legacyWindow = () => win;
export const legacyExternis = () => safe(() => win.externis);
export const legacyModule = (name) => safe(() => (win.require.defined(name) ? win.require(name) : null));
// externis.ctx can be a JSON copy restored from localStorage (Startup.js checkRedirect); the
// 'context' module is the object the legacy code actually mutates.
export const legacyContext = () => legacyModule('context') ?? safe(() => win.externis.ctx);

// Assigning an equal value would still re-render: compare plain snapshots first.
const sameValue = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);
function assign(target, key, value) {
	if (!sameValue(target[key], value)) target[key] = value;
}

// Startup.js areAllSynchronizationsCompleted(): an application synchronization (SALFO, SFA, DCRFO) runs while
// its flag is `true` - OFFLINE-ORDERS holds an enumSynch number and never counts.
export const isLegacySynchronizing = () => Object.values(safe(() => win.externis.synchronizing, {}) ?? {}).includes(true);

// Computed, not inline: #ifm-delegation is hidden by the legacy stylesheet until connectAs() shows it.
const isShown = (el) => Boolean(el) && (safe(() => win.getComputedStyle(el).display) ?? el.style?.display) !== 'none';

function refreshBasics() {
	if (!win) return;
	const ext = legacyExternis();
	const ctx = legacyContext();
	const doc = safe(() => win.document);

	const page = pageFromHash(safe(() => win.location.hash, ''));
	const loggedIn = safe(() => ctx.user.id > 0, false);
	const hiddenApp = Boolean(safe(() => ext.hiddenApp, false));
	const delegationFrameOpen = isShown(safe(() => doc.getElementById('ifm-delegation')));

	assign(legacyNav, 'page', page);
	assign(legacyNav, 'loggedIn', loggedIn);
	assign(legacyNav, 'hiddenApp', hiddenApp);
	assign(legacyNav, 'delegationFrameOpen', delegationFrameOpen);
	assign(
		legacyNav,
		'delegation',
		safe(() => ctx.delegation || null)
	);
	assign(legacyNav, 'env', safe(() => ext.env, '') || '');
	assign(legacyNav, 'version', safe(() => ext.version, '') || '');
	assign(legacyNav, 'isLocalhost', ['localhost', '127.0.0.1'].includes(safe(() => win.location.hostname, '')));
	assign(legacyNav, 'online', isOnline());
	assign(legacyNav, 'updateReady', mainUpdateSignal);

	const user = safe(() => ctx.user, {}) ?? {};
	assign(legacyNav, 'user', { firstName: user.firstName ?? '', lastName: user.lastName ?? '', userName: user.userName ?? '', email: user.email ?? '', organisationLabel: user.organisationLabel ?? '', locale: user.locale ?? '' });
	// The legacy renders the logo inside its own document: resolve it against that URL.
	const logo = safe(() => ctx.customer.logo, null);
	assign(legacyNav, 'customer', { logo: logo ? safe(() => new URL(logo, win.location.href).href, logo) : null, name: safe(() => ctx.customer.name, '') ?? '' });

	assign(legacyNav, 'chromeMode', resolveChromeMode({ attached: true, loggedIn, page, hiddenApp, delegationFrameOpen }));
}

// Role ∩ RoleMenu intersection over Core/Menus, run through legacyMenuRules.js (port of the removed
// sidebar.js rules). getMenus()/getRolesMenu() are memory-cached (src/api/core/menu.js, role.js), so
// re-running this on every relevant amplify event is cheap; menuRequestToken drops a response that
// lost a race with a newer one.
let menuRequestToken = 0;

// The legacy views that list menu entries (vwOrdersTracking.htm: validation / statement) bind to the
// externis.navMenus observable Startup.js declares, formerly the sidebar's itemsSales().
function publishLegacyMenus(menus) {
	const navMenus = safe(() => win.externis.navMenus);
	if (typeof navMenus !== 'function') return;
	const plain = (items) => (items ?? []).map(({ appMenu, label, view }) => ({ appMenu, label, view }));
	safe(() => navMenus({ sfa: plain(menus.sfa), sales: plain(menus.sales), doc: plain(menus.doc) }));
}

function refreshMenus() {
	const ctx = legacyContext();
	const salesHelper = legacyModule('salesHelper');
	const delegation = Boolean(safe(() => ctx.delegation, false));
	const sfa = Boolean(safe(() => salesHelper.isSFAActivated(), false));
	const assortOrderActivated = Boolean(safe(() => salesHelper.isProductAssortOrderActivated(), false));

	assign(legacyNav, 'flags', {
		delegation,
		tokenSales: Boolean(safe(() => ctx.user.tokenSales, false)),
		tokenDoc: Boolean(safe(() => ctx.user.tokenDoc, false)),
		hasAccessToSales: hasAccessToSales(safe(() => ctx.user.processTypes, [])),
		assortOrderActivated,
	});
	assign(legacyNav, 'sfaActivated', sfa);

	const token = ++menuRequestToken;
	Promise.all([getMenus(), getRolesMenu()])
		.then(([menuRows, rolesMenuRows]) => {
			if (token !== menuRequestToken || !win) return;
			const roleMenuIds = safe(() => rolesMenuRows[0].menuIds, []) ?? [];
			const groups = buildMenuGroups({ menuRows: menuRows ?? [], roleMenuIds, delegation, sfa, hasAccessToSales: legacyNav.flags.hasAccessToSales, assortOrderActivated });
			assign(legacyNav, 'menus', { home: HOME_MENU, agenda: groups.agenda, myPos: groups.myPos, sfa: groups.sfa, sales: groups.sales, doc: groups.doc });
			assign(legacyNav, 'menusReady', true);
			publishLegacyMenus(groups);
		})
		.catch((error) => {
			log('warn', 'legacyNav', `refreshMenus failed: ${error.message}`);
			if (token === menuRequestToken && win) assign(legacyNav, 'menusReady', true);
		});
}

// Draft / offline order counters: vwSalesEdito.js checkDraftCart()/checkOfflineOrders() derive them
// from shoppingCart.getDraftList() (which also purges drafts older than 31 days) and storage
// 'OFFLINE-ORDERS' - read that same data directly.
function refreshShortcuts() {
	const cart = legacyModule('shoppingCart');
	const storage = legacyModule('storage');

	safe(() =>
		cart.getDraftList((result) => {
			if (!win) return;
			const count = (result ?? []).length;
			assign(legacyNav, 'shortcuts', { ...legacyNav.shortcuts, draft: { visible: count > 0, count } });
		})
	);

	safe(() =>
		storage.get('OFFLINE-ORDERS', (offlineOrders) => {
			if (!win) return;
			const orders = offlineOrders ?? [];
			const offlineCount = orders.filter((o) => !o._syncError).length;
			const errorCount = orders.filter((o) => o._syncError).length;
			assign(legacyNav, 'shortcuts', { ...legacyNav.shortcuts, offline: { visible: offlineCount > 0, count: offlineCount }, offlineErrors: { visible: errorCount > 0, count: errorCount } });
		})
	);
}

function refreshNavFlags() {
	const ext = legacyExternis();
	assign(legacyNav, 'extensionUpdate', extensionUpdateSignal);
	assign(legacyNav, 'appIsReady', appIsReadySignal);
	assign(legacyNav, 'synchronizing', isLegacySynchronizing());
	// eagle.js vueWidgetsEnabled(): the 'VueWidgets' option, known once the OPTIONS synchronization ran.
	assign(legacyNav, 'vueWidgets', Boolean(safe(() => ext.ecosVM.vueWidgetsEnabled(), false)));
	assign(legacyNav, 'agendaSynchAllowed', agendaSynchAllowedSignal);
	// ext.msal read directly, like readProfile() already does - not through vmNav.msCalendars.
	assign(
		legacyNav,
		'agendaCalendars',
		(safe(() => ext.msal.calendars, []) ?? []).map((c) => ({ id: c.id, name: c.name ?? '', isDefault: Boolean(c.isDefaultCalendar) }))
	);
}

// nav.js refreshStorage(): storage.size(cb) is a module call, not vmNav - legacyNavActions.js's
// refreshStorageSize() calls this directly instead of writing back into vmNav.storageSize.
export function setStorageSize(size) {
	assign(legacyNav, 'storageSize', { sizeBase: size?.sizeBase ?? 0, sizeExtraSales: size?.sizeExtraSales ?? 0, sizeExtraDoc: size?.sizeExtraDoc ?? 0 });
}

// Read from Communication/Notifications directly (src/api/communication/notification.js), like the
// legacy notification.js getNotifications() did. Its timer and side effects (geolocation refresh,
// confetti, browser push, offline warning) live in legacyNotificationPoller.js, which force-refreshes
// the same cache and re-runs this list.
let notificationRequestToken = 0;

function refreshNotifications() {
	const token = ++notificationRequestToken;
	getNotifications()
		.then((raw) => {
			if (token !== notificationRequestToken || !win) return;
			const items = (raw ?? []).filter((n) => n.state === 0).sort((a, b) => b.id - a.id);
			const ids = new Set(items.map((n) => n.id));
			[...dismissedNotifications].forEach((id) => !ids.has(id) && dismissedNotifications.delete(id));

			rawItems.notifications.clear();
			const snapshot = items
				.filter((n) => !dismissedNotifications.has(n.id))
				.map((n) => {
					rawItems.notifications.set(n.id, n);
					return {
						id: n.id,
						type: n.type,
						application: n.application ?? '',
						title: n.title ?? '',
						message: n.data ?? '',
						date: safe(() => win.dateHelper.toLocaleString(new Date(n.createdDate)), '') ?? '',
						icon: NOTIFICATION_ICONS[n.type] ?? 'notifications',
					};
				});
			assign(legacyNav, 'notifications', snapshot);
		})
		.catch((error) => log('warn', 'legacyNav', `refreshNotifications failed: ${error.message}`));
}

// Pure port of the removed visitPreview.js init() (legacyVisitRules.js) + its real side effects:
// obsolete-draft cleanup (sfaHelper.removeDraftAppointment2), missing-POS sync-error logging
// (sfaHelper.saveAppointmentSyncError), rewriting ctx.user.settings' VisitPreparations entry (read back
// by sfaHelper.appointment.isInPrepa) and, like its refreshUI(), activating a pending geoloc bypass code.
const BUCKET_NAMES = { visit: 'current', preparation: 'preparation', late: 'late' };

// visitPreview.js evtAppSwitching(): nothing is classified before the delivery locations, the options
// and the appointments are synchronized, and only for SFA - otherwise every draft looks like a missing POS.
function visitsReady(ext) {
	const synch = safe(() => ext.ecosVM.synchProcess, {}) ?? {};
	return Boolean(synch.DELIVERYLOCATIONS && synch.OPTIONS && synch.SFA_APPOINTMENTS) && Boolean(safe(() => legacyModule('salesHelper').isSFAActivated(), false));
}

export function refreshVisits() {
	const ext = legacyExternis();
	const ctx = legacyContext();
	const storage = legacyModule('storage');
	const sfaHelper = legacyModule('sfaHelper');
	if (!storage || !visitsReady(ext)) return;

	const logos = safe(() => ext.ecosVM.accountsLogosCache, {}) ?? {};
	const deliveryLocations = safe(() => ext.ecosVM.deliveryLocations, []) ?? [];
	const now = new Date();
	const lateLimit = dateOnly(addDays(now, -7));
	const delayAllowedMs = Number(safe(() => sfaHelper.getCreationDelayAllowed(), 0)) || 0;

	safe(() =>
		storage.get('SFA_APPOINTMENTS_LATE', (lateRaw) => {
			if (!win) return;
			storage.get('SFA_APPOINTMENTS', (allRaw) => {
				if (!win) return;
				storage.get('SFA_DRAFT_APPOINTMENT2', (draftRaw) => {
					if (!win) return;

					const allAppointments = allRaw ?? [];
					const inPrepaSetting = safe(() => ctx.user.settings.find((s) => s.groupName === 'WEBAPP' && s.name === 'VisitPreparations'));
					const inPrepaItems = safe(() => JSON.parse(inPrepaSetting.value), []) ?? [];
					const list = buildVisitList({ lateAppointments: lateRaw ?? [], draftAppointments: draftRaw ?? [], inPrepaItems, allAppointments });

					rawItems.visits.clear();
					const buckets = { current: [], preparation: [], late: [] };

					list.forEach((appointment) => {
						const result = classifyAppointment({ appointment, allAppointments, deliveryLocations, lateLimit, now });

						if (result.bucket === 'obsolete' || result.bucket === 'cancelled') {
							safe(() => sfaHelper.removeDraftAppointment2(appointment.uid));
							return;
						}
						if (result.bucket === 'missing-pos') {
							if (!reportedMissingPos.has(appointment.uid)) {
								reportedMissingPos.add(appointment.uid);
								safe(() => sfaHelper.saveAppointmentSyncError('POS not available anymore', appointment));
							}
							return;
						}
						if (result.bucket === 'dropped') return;

						const bucketName = BUCKET_NAMES[result.bucket];
						const key = `${bucketName}:${appointment.uid}`;
						rawItems.visits.set(key, { id: appointment.uid, appointment });
						buckets[bucketName].push({
							key,
							id: appointment.uid,
							label: result.dl.companyName ?? '',
							period: `${result.startDate.toLocaleDateString()} ${result.startDate.toLocaleTimeString().slice(0, 5)}-${result.endDate.toLocaleTimeString().slice(0, 5)}`,
							subtitle: safe(() => sfaHelper.appointment.getDisplayLabel(appointment), '') ?? '',
							// eagle.js loadAccountsLogosInCache(): `main` is the downloaded logo, `thumb` a 35px reduction blurred on a retina screen.
							logo: safe(() => logos[appointment._accountId].main || logos[appointment._accountId].thumb, null),
							ready: bucketName === 'preparation' && isAppointmentReady(appointment, { now, delayAllowedMs }),
							_startTime: result.startDate.getTime(),
						});
					});

					['current', 'preparation', 'late'].forEach((name) => {
						buckets[name].sort((a, b) => a._startTime - b._startTime);
						buckets[name].forEach((item) => delete item._startTime);
					});

					assign(legacyNav, 'visits', buckets);

					safe(() => {
						const preparationValue = JSON.stringify(buckets.preparation.map((item) => rawItems.visits.get(item.key)));
						ctx.user.settings = (ctx.user.settings ?? []).filter((s) => !(s.groupName === 'WEBAPP' && s.name === 'VisitPreparations'));
						ctx.user.settings.push({ groupName: 'WEBAPP', name: 'VisitPreparations', value: preparationValue });
					});

					clearTimeout(geoCodeTimer);
					geoCodeTimer = setTimeout(() => safe(() => sfaHelper.evalGeoCode()), 150);
				});
			});
		})
	);
}

const dlLabel = (ecosVM, dlId) => safe(() => ecosVM.deliveryLocations.find((dl) => dl.id === dlId).companyName, null) || 'N/A';

function locationFranco(location, products) {
	const ext = legacyExternis();
	const salesHelper = legacyModule('salesHelper');
	const cart = legacyModule('shoppingCart');
	// cartPreview.js getFranco(): no franco while the assortment catalogue is not loaded.
	if (!safe(() => ext.ecosVM.productsAssort.length, 0)) return null;

	const company = safe(() => salesHelper.companies.getCompany(location.companyId));
	const items = products.filter((p) => p.apCompanyId === location.companyId && p.apDlId === location.dlId);
	const franco = safe(() => salesHelper.companies.calcFranco(company, items, location.dlId, cart));
	return francoSummary({ company, franco, currency: safe(() => salesHelper.getCurrency(), '') ?? '', isReturn: items[0]?.orderType === ASSORT_RETURN_ORDER_TYPE });
}

function refreshCart() {
	const cart = legacyModule('shoppingCart');
	const ecosVM = safe(() => legacyExternis().ecosVM);
	const products = safe(() => cart.getProducts(), []) ?? [];
	const grouped = groupCartProducts(products);

	rawItems.cart.clear();
	const usedKeys = new Map();
	const line = (product) => {
		const base = [product.id, product.campaignId ?? 0, product.rswId ?? 0, product.apCompanyId ?? 0, product.apDlId ?? 0, product.apIsFree ? 1 : 0, product._dateFillingIndex ?? ''].join('-');
		const n = usedKeys.get(base) ?? 0;
		usedKeys.set(base, n + 1);
		const key = n ? `${base}#${n}` : base;
		rawItems.cart.set(key, product);
		return { key, id: product.id, name: product.name ?? '', reference: product.reference ?? '', quantity: cartQuantityText(product), free: Boolean(product.apIsFree), process: product.process ?? 0, thumbUid: product.imageThumbnailUid ?? null, hasImage: Boolean(product.imageUid) };
	};

	assign(legacyNav, 'cart', {
		count: grouped.count,
		regular: grouped.regular.map(line),
		rsw: grouped.rsw.map(line),
		assort: grouped.assort.map((company) => ({
			companyId: company.companyId,
			companyName: company.companyName,
			locations: company.locations.map((location) => ({ key: location.key, companyId: location.companyId, dlId: location.dlId, label: dlLabel(ecosVM, location.dlId), franco: locationFranco(location, products), items: location.items.map(line) })),
		})),
	});
}

function run(steps) {
	if (!win) return;
	steps.forEach((fn) => {
		try {
			fn();
		} catch (error) {
			log('warn', 'legacyNav', `${fn.name} failed: ${error.message}`);
		}
	});
}

export const refreshLegacyNav = () => run([refreshBasics, refreshMenus, refreshShortcuts, refreshNavFlags, refreshNotifications, refreshVisits, refreshCart]);

// Optimistic local removal right after markNotificationRead() resolves - the list itself only
// re-syncs with the backend on the next refreshNotifications() (a relevant amplify event, the panel
// opening, or a manual reload), same trade-off as everywhere else this cache is read from.
export function dismissNotification(id) {
	dismissedNotifications.add(id);
	refreshNotifications();
}

// Cheap reads only: the panel lists are event-driven (amplify topics, not KO subscriptions any more).
// Menus are refreshed from amplify events only (evtAppSwitching/evtUserChanged/evtSynchroEnd trigger
// refreshLegacyNav through scheduleRefresh) - no need to re-fetch/rebuild them every second.
const poll = () => run([bindAmplify, refreshBasics, refreshShortcuts, refreshNavFlags]);

function scheduleRefresh(delay = REFRESH_DEBOUNCE_MS) {
	clearTimeout(refreshTimer);
	refreshTimer = setTimeout(refreshLegacyNav, delay);
}

function bindAmplify() {
	const amplify = safe(() => win.amplify);
	if (!amplify?.subscribe || boundAmplify === amplify) return;

	const onChange = () => scheduleRefresh();
	const onChangeView = (vw) => {
		scheduleRefresh();
		if (safe(() => vw.view) === 'widgets') checkReleaseNotes();
	};
	const onAppSwitching = () => {
		scheduleRefresh();
		clearTimeout(settleTimer);
		settleTimer = setTimeout(refreshLegacyNav, APP_SWITCH_SETTLE_MS);
	};
	const onMainUpdate = () => {
		mainUpdateSignal = true;
		scheduleRefresh();
	};
	// nav.js evtSynchroEnd(): appIsReady only ever flips true for a SALFO (SFA) sync end, which is also
	// where notification.js (re)started its poll.
	const onSynchroEnd = (e) => {
		if (safe(() => e.sender) === 'SALFO') {
			appIsReadySignal = true;
			pollLegacyNotificationsNow();
		}
		onAppSwitching();
	};
	const onExtensionUpdateNeeded = () => {
		extensionUpdateSignal = true;
		scheduleRefresh();
	};
	const onAgendaSynchActivated = (params) => {
		agendaSynchAllowedSignal = Boolean(safe(() => params.active, false));
		scheduleRefresh();
	};

	amplifyHandlers = [
		['evtAppSwitching', onAppSwitching],
		['evtSynchroEnd', onSynchroEnd],
		['evtUserChanged', onChange],
		['evtMainUpdateNeeded', onMainUpdate],
		['evtShoppingCardChanged', onChange],
		['evtVisitChanged', onChange],
		['evtOfflineOrderChanged', onChange],
		['evtChangeView', onChangeView],
		['evtUpdateNeeded', onExtensionUpdateNeeded],
		['evtAgendaSynchActivated', onAgendaSynchActivated],
		['evtCalendarMSAL', onChange],
	];
	amplifyHandlers.forEach(([topic, fn]) => safe(() => amplify.subscribe(topic, fn)));
	boundAmplify = amplify;
}

function unbindAmplify() {
	if (boundAmplify) amplifyHandlers.forEach(([topic, fn]) => safe(() => boundAmplify.unsubscribe(topic, fn)));
	boundAmplify = null;
	amplifyHandlers = [];
}

function observeDom() {
	const doc = safe(() => win.document);
	const Observer = safe(() => win.MutationObserver);
	if (!doc || !Observer) return;

	const watch = (el, options) => {
		if (!el) return;
		const observer = new Observer(() => scheduleRefresh(REFRESH_DEBOUNCE_MS));
		observer.observe(el, options);
		observers.push(observer);
	};
	watch(doc.getElementById('ifm-delegation'), { attributes: true, attributeFilter: ['style'] });
}

// vwWidgets.js checkReleaseNotes() (removed with nav.htm's #mdReleaseNote): when the home view opens,
// a NEW-RELEASE-NOTE cookie (or the debug flag) shows the release notes once, unless a new version
// is still being installed. The cookie is left alone while there is no chrome to show it in.
const RELEASE_NOTE_COOKIE = 'NEW-RELEASE-NOTE';

function checkReleaseNotes() {
	const ext = legacyExternis();
	const cookies = safe(() => win.cookieHelper);
	if (legacyNav.chromeMode === 'hidden' || safe(() => ext.NEW_RELEASE, false)) return;
	if (!safe(() => cookies.read(RELEASE_NOTE_COOKIE)) && !safe(() => ext.debug, false)) return;

	safe(() => cookies.delete(RELEASE_NOTE_COOKIE));
	legacyNav.requestedDialog = 'releaseNotes';
}

// Called by iframeWatcher once amplify and externis exist in a freshly loaded legacy document.
export function attachLegacyNavState(iframeWindow) {
	if (!iframeWindow) return;
	if (win !== iframeWindow) detachLegacyNavState();
	if (win === iframeWindow && legacyNav.attached) return;

	win = iframeWindow;
	legacyNav.attached = true;

	// hasher navigations (login → portal → application) switch the chrome before the next paint.
	hashListener = () => refreshBasics();
	safe(() => win.addEventListener('hashchange', hashListener));

	attachLegacyNotificationPoller(win, { getContext: legacyContext, onRefreshed: refreshNotifications });
	stopConnectivityWatch = onConnectivityChange(() => scheduleRefresh());
	bindAmplify();
	observeDom();
	refreshLegacyNav();

	pollTimer = setInterval(poll, POLL_MS);
}

// Called on every iframe load, before anything reads the new document.
export function detachLegacyNavState() {
	clearInterval(pollTimer);
	clearTimeout(refreshTimer);
	clearTimeout(settleTimer);
	clearTimeout(geoCodeTimer);
	pollTimer = refreshTimer = settleTimer = geoCodeTimer = null;

	if (win && hashListener) safe(() => win.removeEventListener('hashchange', hashListener));
	hashListener = null;
	stopConnectivityWatch?.();
	stopConnectivityWatch = null;
	detachLegacyNotificationPoller();
	resetLegacyDialogs();
	observers.forEach((observer) => safe(() => observer.disconnect()));
	observers = [];
	unbindAmplify();

	win = null;
	mainUpdateSignal = false;
	extensionUpdateSignal = false;
	appIsReadySignal = false;
	agendaSynchAllowedSignal = false;
	dismissedNotifications.clear();
	reportedMissingPos.clear();
	Object.values(rawItems).forEach((map) => map.clear());
	Object.assign(legacyNav, initialState());
}
